import { MutableRefObject, useEffect, useState } from "react";
import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import type { Table as TableType } from "@tanstack/table-core";
import { PaginationState } from "@tanstack/table-core";
import { useDebounceValue } from "../../../core";
import { ApplyTablePredefinedFilterOptions, FetchTableData } from "../types";

type UseTableDataOptions<TData extends object, TPredefinedFilter extends unknown = unknown> = {
  data?: TData[];
  fetchData?: FetchTableData<TData, TPredefinedFilter>,
  pagination: PaginationState,
  sorting?: SortingState,
  filters?: ColumnFiltersState,
  debounceTimeout?: number,
  globalFilter?: string | null,
  tableRef?: MutableRefObject<TableType<TData>>
  predefinedFilter?: TPredefinedFilter,
  applyPredefinedFilter?: (options: ApplyTablePredefinedFilterOptions<TData, TPredefinedFilter>) => TData[],
}

export type UseTableDataResult<T extends object> = {
  isFetching: boolean,
  data: T[]
  rowCount?: number
  pageCount?: number
}

export function useTableData<TData extends object, TPredefinedFilter extends unknown = unknown>(
  {
    data,
    fetchData,
    pagination,
    sorting,
    filters,
    debounceTimeout = 0,
    globalFilter,
    tableRef,
    predefinedFilter,
    applyPredefinedFilter,
  }: UseTableDataOptions<TData, TPredefinedFilter>): UseTableDataResult<TData> {
  const [ tableData, setTableData ] = useState<UseTableDataResult<TData>>({
    data: [],
    pageCount: undefined,
    rowCount: undefined,
    isFetching: true,
  });

  const debouncedPagination = useDebounceValue(pagination, debounceTimeout);
  const debouncedSorting = useDebounceValue(sorting, debounceTimeout);
  const debouncedFilters = useDebounceValue(filters, debounceTimeout);
  const debouncedGlobalFilter = useDebounceValue(globalFilter, debounceTimeout);

  useEffect(() => {
    if (fetchData) {
      setTableData(prev => ({ ...prev, isFetching: true }));
      const abortController = new AbortController();
      new Promise<void>(async (resolve) => {
        try {
          const result = await fetchData({
            ...debouncedPagination,
            setTableData,
            table: tableRef!.current!,
            sorting: debouncedSorting || [],
            filters: debouncedFilters || [],
            signal: abortController.signal,
            globalFilter: debouncedGlobalFilter || null,
            predefinedFilter: predefinedFilter || null,
          });
          if (!abortController.signal.aborted) {
            setTableData({
              data: result.items,
              pageCount: result.totalPages,
              rowCount: result.totalElements,
              isFetching: false,
            });
          }
        } finally {
          resolve();
        }
      });
      return () => {
        abortController.abort(new Error("Operation aborted by table", { cause: "USER_ABORT" }));
      };
    }
  }, [ fetchData, debouncedPagination, debouncedSorting, debouncedFilters, debouncedGlobalFilter, tableRef, predefinedFilter ]);

  useEffect(() => {
    if (!fetchData) {
      setTableData({
        data: (predefinedFilter ? applyPredefinedFilter?.({ data, filter: predefinedFilter }) : data) || [],
        pageCount: undefined,
        rowCount: undefined,
        isFetching: false,
      });
    }
  }, [ fetchData, data, predefinedFilter, applyPredefinedFilter ]);

  return tableData;
}